import type Vue from "vue";
import { getCurrentInstance } from "vue";
import { useAppStore } from "@/store";
import { useLegacyRootEvent } from "./useLegacyRootEvent";

export type RouterPath = {
  domainId: string;
  topicId: string;
  messageId: string;
  commentId: string;
};

/**
 * URL コピー処理（旧 `ClipboardCopyMixin` の composable 版）。
 *
 * iOS Safari のクリップボード API 挙動差に依存するため、`navigator.clipboard` /
 * `navigator.permissions` の分岐ロジックは旧 mixin と同一に保つこと。
 * トースト表示は BootstrapVue の `$bvToast`（Step 3 で composable 化予定）、
 * 失敗通知は `$root.$emit('failed-url-copy')`（1-E/PR-4 で差し替え予定）を用いる。
 *
 * 1-D（mixin → composable）。利用側の書き換えは 1-C 高リスク群（PR-3）で実施する。
 */
export function useClipboardCopy() {
  const rootEvent = useLegacyRootEvent();
  // $bvToast は BootstrapVue が Vue インスタンスへ生やす（Step 3 で composable 化予定）
  const vm = getCurrentInstance()?.proxy as Vue | undefined;

  const getTargetType = (messageId: string, commentId: string): string => {
    if (!messageId && !commentId) {
      return "topic";
    } else if (messageId && !commentId) {
      return "message";
    } else if (messageId && commentId) {
      return "comment";
    } else {
      return "";
    }
  };

  const getWritePath = (path: RouterPath): string => {
    const type = getTargetType(path.messageId, path.commentId);
    switch (type) {
      case "topic":
        return `${window.location.origin}/${path.domainId}/${path.topicId}`;
      case "message":
        return `${window.location.origin}/${path.domainId}/${path.topicId}/${path.messageId}`;
      case "comment":
        return `${window.location.origin}/${path.domainId}/${path.topicId}/${path.messageId}/${path.commentId}`;
      default:
        return "";
    }
  };

  const enableUrlCopy = (messageId: string, commentId: string): boolean => {
    const type = getTargetType(messageId, commentId);
    switch (type) {
      case "topic":
        return !!navigator.clipboard;
      case "message": {
        if (!navigator.clipboard) return false;
        const app = useAppStore();
        return !!app.domainId && !!app.topicId;
      }
      case "comment": {
        if (!navigator.clipboard) return false;
        const app = useAppStore();
        return !!app.domainId && !!app.topicId && !!messageId;
      }
      default:
        return false;
    }
  };

  const onUrlCopy = (path: RouterPath): void => {
    if (!navigator.permissions || !navigator.clipboard) {
      // Android + ios13.4 safariより下用
      // Android: navigator.permissions非対応
      // ios13.4より下: navigator.clipboard (navigator.permissionsはios safari非対応)
      const tempInput = document.createElement("input");
      tempInput.value = getWritePath(path);
      tempInput.setAttribute(
        "style",
        "position: absolute; left: -1000px; top: -1000px;",
      );
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand("copy");
      document.body.removeChild(tempInput);
      vm?.$bvToast.show("urlToast");
      return;
    } else if (!enableUrlCopy(path.messageId, path.commentId)) {
      rootEvent.emit("failed-url-copy");
      return;
    }
    navigator.clipboard
      .writeText(getWritePath(path))
      .then(() => vm?.$bvToast.show("urlToast"))
      .catch(() => rootEvent.emit("failed-url-copy"));
  };

  return {
    getTargetType,
    getWritePath,
    enableUrlCopy,
    onUrlCopy,
  };
}
